import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import { ShieldCheck, FileText, Calendar, ListCheck, ArrowRight } from 'lucide-react';
import { isFreeSubscription, readUserFromStorage } from '../../utils/subscription';
import { showWarning } from '../../utils/toast';
import '../../App.css';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
}

export default function AuthorityCopilotPlanPage() {
  const navigate = useNavigate();
  const [user] = useState(() => readUserFromStorage());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const planBlocked = isFreeSubscription(user);

  useEffect(() => {
    if (!planBlocked) return;
    showWarning(
      'Authority Setup Copilot is available on Owner-Operator and Fleet/Pro plans. Upgrade to unlock.',
    );
    navigate('/authority', { replace: true });
  }, [planBlocked, navigate]);

  useEffect(() => {
    if (planBlocked) return;
    const fetchPlan = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/authority-copilot/plan`, getAuthHeaders());
        setPlan(res.data);
      } catch (err) {
        console.error('Fetch plan error:', err);
        setPlan(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [planBlocked]);

  if (planBlocked) {
    return null;
  }

  if (loading) {
    return (
      <div className="copilot-plan-page">
        <div className="copilot-loading">Loading your plan...</div>
      </div>
    );
  }

  if (!plan?.planOutput) {
    return (
      <div className="copilot-plan-page">
        <div className="copilot-plan-empty card">
          <ShieldCheck size={48} color="var(--primary)" />
          <h2>No plan yet</h2>
          <p>Complete the Authority Setup Copilot to get your personalized setup plan, filings checklist, timeline, and next actions.</p>
          <button type="button" className="button primary" onClick={() => navigate('/authority-copilot')}>
            Start Copilot
          </button>
        </div>
      </div>
    );
  }

  const out = plan.planOutput;

  return (
    <div className="copilot-plan-page">
      <div className="copilot-plan-header">
        <h1><ShieldCheck size={32} /> Your Authority Setup Plan</h1>
        <p className="copilot-plan-tagline">Education-only. Verify all requirements with FMCSA and your state.</p>
      </div>

      <div className="copilot-plan-sections">
        <section className="copilot-plan-section card">
          <h2><ListCheck size={22} /> Personalized setup plan</h2>
          <ul className="copilot-plan-list">
            {(out.personalizedPlan || []).map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="copilot-plan-section card">
          <h2><FileText size={22} /> Required filings checklist</h2>
          <p className="copilot-disclaimer-small">Estimates for reference only. Confirm with official sources.</p>
          <table className="copilot-filings-table">
            <thead>
              <tr>
                <th>Filing</th>
                <th>Source</th>
                <th>Est. cost</th>
              </tr>
            </thead>
            <tbody>
              {(out.requiredFilings || []).map((row, i) => (
                <tr key={i}>
                  <td>{row.item}</td>
                  <td>{row.source}</td>
                  <td>{row.estimatedCost ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="copilot-plan-section card">
          <h2><Calendar size={22} /> Timeline & cost estimator</h2>
          <p className="copilot-disclaimer-small">For planning only. Actual timelines and costs vary.</p>
          <div className="copilot-timeline-cost">
            {out.timelineWeeks != null && (
              <p><strong>Typical timeline:</strong> ~{out.timelineWeeks} weeks (varies by state and carrier)</p>
            )}
            {out.estimatedCostRange && (
              <p>
                <strong>Estimated cost range:</strong> ${out.estimatedCostRange.min.toLocaleString()} – ${out.estimatedCostRange.max.toLocaleString()} (one-time startup; ongoing costs vary)
              </p>
            )}
          </div>
        </section>

        <section className="copilot-plan-section card">
          <h2><ArrowRight size={22} /> Recommended next actions in Sovrynn</h2>
          <ul className="copilot-next-actions">
            {(out.nextActionsInSovrynn || []).map((action, i) => (
              <li key={i}>
                <button
                  type="button"
                  className="copilot-next-action-btn"
                  onClick={() => navigate(action.path)}
                >
                  {action.label} <ArrowRight size={16} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="copilot-plan-footer">
        <button type="button" className="button secondary" onClick={() => navigate('/authority-copilot')}>
          Redo Copilot
        </button>
      </div>
    </div>
  );
}
